import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography, useTheme } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import EditIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreOutlined";
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOffOutlined';
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { tokens } from "../../theme";
import { api, apiPatients, IPatient, IPatientSearchRequest, patientsBaseRoute } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import DataGridNoRowsOverlay from '../../components/DataGridNoRowsOverlay';
import Header from "../../components/Header";

const patientSearchRequestPrototype : IPatientSearchRequest = {
  personalHealthNumber: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: null
}

const fetchPatientList = async (request: IPatientSearchRequest): Promise<IPatient[]> => {
  const response = await fetch(`${api}${apiPatients}search`, {
    method: 'POST',
    headers: createApiHeaders(true, true),
    body: JSON.stringify({
      ...request, 
      dateOfBirth: request.dateOfBirth ? dayjs(request.dateOfBirth).format('YYYY-MM-DD') : request.dateOfBirth
    })
  });

  if (response.ok) {
    const json = await response.json();
    return json;
  }

  throw new Error(`Detailed Error Code: ${response.status}.`);
}

const PatientList = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [isLoading, setIsLoading] = useState(false);
  const [labelText, setLabelText] = useState("NO PATIENTS");
  const [patients, setPatients] = useState<IPatient[]>([]);

  const setAfterLoadingState = (success: boolean, rowsCount: number, error?: string): void => { 
    setIsLoading(false);
    if (success) {
      setLabelText(rowsCount === 0 ? "NO PATIENTS FOUND! SEARCH AGAIN" : "");
    }
    else {
      setLabelText(`ERROR! ${error}`);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...patientSearchRequestPrototype,
      dateOfBirth: null as Dayjs|null
    },
    enableReinitialize: true, 
    onSubmit: (values) => {
      setIsLoading(true);
      fetchPatientList({ ...values, dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toDate() : null })
      .then((response) => { 
        const patientsCollection = response as IPatient[];
        setPatients(patientsCollection);
        setAfterLoadingState(true, patientsCollection.length, undefined);
      })
      .catch((reason) => {
        setAfterLoadingState(false, 0, reason);
      });
    }
  });

  const clearSearchFilters = () => {
    formik.setValues({ ...patientSearchRequestPrototype, dateOfBirth: null as Dayjs|null });
    setPatients([]);
  };

  const columns : GridColDef[] = [
    {
      field: "personalHealthNumber",
      headerName: "PHN",
      flex: 0.37
    },
    {
      field: "firstName",
      headerName: "First Name",
      flex: 0.75
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 0.75
    },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      flex: 0.37
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1
    },
    {
      field: "mobilePhone",
      headerName: "Mobile Phone (Main)",
      flex: 0.63
    },
    {
      field: "homePhone",
      headerName: "Home Phone",
      flex: 0.37
    },
    {
      field: "workPhone",
      headerName: "Work Phone",
      flex: 0.37
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "right",
      sortable: false,
      filterable: false,
      flex: 0.37,
      renderCell: ({ row: { id } }) => {
        return (
          <Box
            m="0 auto"
            p="6px"
            display="flex" 
            justifyContent="end" 
          >
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontSize: "12px",
                fontWeight: "bold", 
                padding: "8px 14px",
              }} 
              onClick={() => navigate(`${patientsBaseRoute}/${id}`)}
            >
              <EditIcon sx={{ mr: "8px" }} />
              Edit
            </Button>
          </Box>
        );
      }
    }
  ];

  return (
    <Box m="14px">
      <Header title="PATIENTS" subtitle="SEARCH FOR PATIENTS USING BELOW FILTERS" />
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography color={colors.greenAccent[500]} variant="h5">
            SEARCH FILTERS
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Box
                display="grid"
                gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                gap="20px"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 6" }
                }}
              >
                <TextField
                  fullWidth
                  variant="filled"
                  id="personalHealthNumber"
                  name="personalHealthNumber"
                  label="Health Number"
                  color="info"
                  value={formik.values.personalHealthNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  color="info"
                  value={formik.values.firstName}
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur}
                  sx={{ gridColumn: "span 2" }}
                />
                
                <TextField
                  fullWidth
                  variant="filled"
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  color="info"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ gridColumn: "span 2" }}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name="dateOfBirth"
                    label="Date of Birth"
                    value={formik.values.dateOfBirth}
                    onChange={(value) => {
                      formik.setFieldValue("dateOfBirth", value)
                    }}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        variant: "filled",
                        color: "info"
                      }
                    }}
                    sx={{ gridColumn: "span 2" }}
                  />
                </LocalizationProvider>

                <TextField
                  fullWidth
                  variant="filled"
                  id="email"
                  name="email"
                  label="Email"
                  color="info"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  id="phone"
                  name="phone"
                  label="Phone"
                  color="info"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>
            
              <Box display="flex" justifyContent="end" mt="20px">
                <Button 
                  sx={{ 
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100],
                    fontSize: "13px",
                    fontWeight: "bold",
                    width: "125px", 
                    justifyContent: "center",
                    mr: "12px"
                  }}
                  onClick={clearSearchFilters}
                >
                  <SearchOffIcon sx={{ mr: "6px" }} />
                  Clear
                </Button>

                <Button 
                  type="submit" 
                  sx={{ 
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "13px",
                    fontWeight: "bold",
                    width: "125px",
                    justifyContent: "center"
                  }}
                >
                  <SearchIcon sx={{ mr: "6px" }} />
                  Search
                </Button>
              </Box>
            </form>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box
        display="grid"
        m="6px 0 0 0"
        height="45vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid 
          rows={patients} 
          columns={columns} 
          loading={isLoading} 
          slots={{
            noRowsOverlay: () => <DataGridNoRowsOverlay labelText={labelText} />
          }}
          slotProps={{ 
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            } 
          }} 
        />
      </Box>
    </Box>
  );
};

export default PatientList;